import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Select,
  IconButton,
} from "@mui/material";
import { Rnd } from "react-rnd";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";

import Layout from "../Layout/Layout";

async function getCallData(id) {
  try {
    let result = await axios.get(
      `${process.env.REACT_APP_BACKEND_DOMAIN_API}/cms_catalogs/info/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_X_API_KEY,
          "x-access-token": sessionStorage.getItem("accessToken"),
        },
      }
    );
    return result.data.data;
  } catch (error) {
    console.log(`bigc-online-catalogs: ${error}`);
  }
}

export default function HotspotEditorPage() {
  const { catalogId } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [page, setPage] = useState(1);
  const [hotspots, setHotspots] = useState([]);
  const containerRef = useRef();
  const [isDirty, setIsDirty] = useState(false); // Flag ว่ามีการแก้ไข Hotspot หรือยัง
  const [pendingPage, setPendingPage] = useState(null); // หน้าที่ user จะเปลี่ยนไป (ยังไม่เปลี่ยน)
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      setHotspots((prev) => [...prev]); // force re-render with updated size
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  // โหลด catalog
  useEffect(() => {
    if (!catalogId) return;
    getCallData(catalogId).then((data) => {
      setCatalog(data);
      setPage(1); // โหลดหน้าแรกเสมอ
    });
  }, [catalogId]);

  // โหลด Hotspot เมื่อเปลี่ยนหน้า หรือ catalogId
  useEffect(() => {
    setHotspots([]); // clear ของเก่า กันกรณีโหลดช้า
    setIsDirty(false);

    if (!catalogId || !page) return;
    let isCurrent = true;
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_DOMAIN_API}/cms_hotspots/list?catalog_id=${catalogId}&page=${page}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY,
            "x-access-token": sessionStorage.getItem("accessToken"),
          },
        }
      )
      .then((res) => {
        if (isCurrent) setHotspots(res.data || []);
      });
    return () => {
      isCurrent = false;
    };
  }, [catalogId, page]);

  if (!catalog) return <div>Loading...</div>;
  // ทุกครั้งที่ hotspots เปลี่ยน (user ขยับ hotspot หรือแก้ไข link) → set isDirty เป็น true
  const updateHotspot = (i, fields) => {
    setHotspots((prev) => {
      const newList = [...prev];
      newList[i] = { ...newList[i], ...fields };
      return newList;
    });
    setIsDirty(true);
  };
  const addHotspot = () => {
    setHotspots([
      ...hotspots,
      { x: 0.1, y: 0.1, width: 0.2, height: 0.1, link: "" },
    ]);
    setIsDirty(true);
  };
  const deleteHotspot = (i) => {
    setHotspots((prev) => prev.filter((_, idx) => idx !== i));
    setIsDirty(true);
  };

  // เวลาจะเปลี่ยนหน้า
  const handlePageChange = (nextPage) => {
    if (isDirty) {
      setPendingPage(nextPage);
      setShowDialog(true);
    } else {
      setPage(nextPage);
    }
  };

  // กดยืนยันบันทึก
  const handleSaveAndChangePage = async () => {
    await saveHotspots(); // บันทึกก่อน
    setIsDirty(false);
    setShowDialog(false);
    setPage(pendingPage);
    setPendingPage(null);
  };

  // กดไม่บันทึก
  const handleDiscardAndChangePage = () => {
    setIsDirty(false);
    setShowDialog(false);
    setPage(pendingPage);
    setPendingPage(null);
    // (optionally reload hotspot จาก server)
  };

  // กดยกเลิก ไม่เปลี่ยนหน้า
  const handleCancelChangePage = () => {
    setShowDialog(false);
    setPendingPage(null);
  };

  // บันทึก Hotspot
  const saveHotspots = async () => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_DOMAIN_API}/cms_hotspots/save`,
      {
        catalog_id: catalogId,
        page,
        hotspots,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_X_API_KEY,
          "x-access-token": sessionStorage.getItem("accessToken"),
        },
      }
    );
    alert("บันทึกสำเร็จ");
  };

  // คำนวณขนาด container
  const getContainerSize = () =>
    containerRef.current
      ? {
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        }
      : { width: 1, height: 1 };

  return (
    <Layout>
      <Paper sx={{ height: "100%", width: "100%", minWidth: "800px" }}>
        <Box p={4}>
          <Typography variant="h5">
            Hotspot Editor - {catalog.name} - หน้า {page}
          </Typography>
          <>
            <Alert severity="info">
              <AlertTitle>Preview ==&gt; 
              <a href={`${process.env.REACT_APP_WEB_CONNECT_DOMAIN}/catalog/${catalog.friendly_url}`} target="_bank">
                <strong>web-connect (New) : {`${process.env.REACT_APP_WEB_CONNECT_DOMAIN}/catalog/${catalog.friendly_url}`}</strong>
              </a></AlertTitle>
            </Alert>
          </>
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Typography fontWeight="bold">เลือกหน้า:</Typography>
            <Select
              value={page}
              onChange={(e) => handlePageChange(Number(e.target.value))}
              size="small"
            >
              {Array.from({ length: catalog.pages }).map((_, i) => (
                <MenuItem key={i} value={i + 1}>
                  หน้า {i + 1}
                </MenuItem>
              ))}
            </Select>

            <Button onClick={addHotspot} variant="outlined" size="small">
              ➕ เพิ่ม Hotspot
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={saveHotspots}
              size="small"
            >
              💾 บันทึก Hotspot
            </Button>
          </Box>
          <Divider sx={{ my: 3 }} />
          {/*<Box
            position="relative"
            width="100%"
            sx={{ aspectRatio: "1 / 1.41", maxWidth: 800, mx: "auto" }}
            ref={containerRef}
          >
            <img
              src={catalog.image_paths + `/page-${page}.jpg`}
              alt={`Page ${page}`}
              style={{ width: "100%", height: "100%", display: "block" }}
            />*/}
          <Box
            position="relative"
            width={catalog.image_width || 700}
            height={catalog.image_height || 1000}
            mx="auto"
            ref={containerRef}
          >
            <img
              src={catalog.image_paths + `/page-${page}.jpg`}
              alt={`Page ${page}`}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
            {hotspots.map((h, i) => (
              <Rnd
                key={i}
                bounds="parent"
                size={{
                  width: `${h.width * 100}%`,
                  height: `${h.height * 100}%`,
                }}
                position={{
                  x: h.x * getContainerSize().width,
                  y: h.y * getContainerSize().height,
                }}
                onDragStop={(e, d) => {
                  const { width, height } = getContainerSize();
                  updateHotspot(i, {
                    x: d.x / width,
                    y: d.y / height,
                  });
                }}
                onResizeStop={(e, dir, ref, delta, pos) => {
                  const { width, height } = getContainerSize();
                  updateHotspot(i, {
                    width: ref.offsetWidth / width,
                    height: ref.offsetHeight / height,
                    x: pos.x / width,
                    y: pos.y / height,
                  });
                }}
                style={{ zIndex: 2 }}
              >
                <Box
                  sx={{
                    bgcolor: "rgba(255,0,0,0.2)",
                    border: "2px solid red",
                    p: 0.5,
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="ลิงก์"
                    value={h.link}
                    onChange={(e) => updateHotspot(i, { link: e.target.value })}
                    sx={{ width: "100%", bgcolor: "#fff" }}
                  />
                  <IconButton
                    aria-label="ลบ"
                    size="small"
                    sx={{ color: "#fff" }}
                    onClick={() => deleteHotspot(i)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Rnd>
            ))}
          </Box>
        </Box>
      </Paper>
      {/* Dialog เตือนบันทึก */}
      <Dialog open={showDialog}>
        <DialogTitle>ยังไม่ได้บันทึก Hotspot</DialogTitle>
        <DialogContent>
          <p>คุณต้องการบันทึกการเปลี่ยนแปลง Hotspot ก่อนเปลี่ยนหน้าหรือไม่?</p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSaveAndChangePage}
            variant="contained"
            color="primary"
          >
            บันทึกและเปลี่ยนหน้า
          </Button>
          <Button onClick={handleDiscardAndChangePage} color="error">
            ไม่บันทึก
          </Button>
          <Button onClick={handleCancelChangePage}>ยกเลิก</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
